import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { BreadcrumbComponent, BreadcrumbItem } from '../../shared/components/breadcrumb/breadcrumb.component';
import { BreadcrumbService } from '../../shared/components/breadcrumb/service/breadcrumb.service';
import { CompanyService } from '../../core/services/api-services/company.service';

declare var bootstrap: any;

interface ReportType {
  id: string;
  name: string;
  icon: string;
  description: string;
  color: string;
}

interface Company {
  companyId: number;
  companyName: string;
}

interface ReportFilter {
  reportType: string;
  companyId?: number;
  startDate: string;
  endDate: string;
}

@Component({
  selector: 'app-ops-risk-and-compliance',
  standalone: true,
  imports: [CommonModule, FormsModule, BreadcrumbComponent],
  templateUrl: './ops-risk-and-compliance.component.html',
  styleUrl: './ops-risk-and-compliance.component.css'
})
export class OpsRiskAndComplianceComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private offcanvasInstance: any;

  dataSubscriber$!: Subscription;
  breadcrumbItems: BreadcrumbItem[] = [];

  reportTypes: ReportType[] = [
    {
      id: 'wage-withdrawal-fee',
      name: 'Wage Withdrawal Fee Report',
      icon: 'bi bi-cash-stack',
      description: 'Track employee wage withdrawal fees and charges',
      color: '#4CAF50'
    },
    {
      id: 'rollover-fee',
      name: 'Rollover Fee Report',
      icon: 'bi bi-arrow-repeat',
      description: 'Monitor rollover fees and related transactions',
      color: '#2196F3'
    },
    {
      id: 'penalties',
      name: 'Penalties Report',
      icon: 'bi bi-exclamation-triangle',
      description: 'View all penalties and violations',
      color: '#FF9800'
    },
    {
      id: 'total-cost-funds',
      name: 'Total Cost of Funds',
      icon: 'bi bi-calculator',
      description: 'Complete cost analysis for all funds',
      color: '#9C27B0'
    },
    {
      id: 'paid-cost-funds',
      name: 'Paid Cost of Funds',
      icon: 'bi bi-check-circle',
      description: 'Settled and paid fund costs overview',
      color: '#4CAF50'
    },
    {
      id: 'balance-cost-funds',
      name: 'Balance Cost of Funds',
      icon: 'bi bi-wallet2',
      description: 'Outstanding balance on fund costs',
      color: '#E91E63'
    },
    {
      id: 'bank-wage-reserved',
      name: 'Bank - Wage Reserved Account',
      icon: 'bi bi-bank',
      description: 'Reserved wage account balances and transactions',
      color: '#3F51B5'
    },
    {
      id: 'employee-wallet-balance',
      name: 'Employee Wallet Balance',
      icon: 'bi bi-person-badge',
      description: 'Current employee wallet balances',
      color: '#00BCD4'
    },
    {
      id: 'trade-receivables',
      name: 'Trade Receivables from Employers',
      icon: 'bi bi-file-earmark-text',
      description: 'Outstanding receivables from employer accounts',
      color: '#795548'
    },
    {
      id: 'payable-funder',
      name: 'Payable to the Funder',
      icon: 'bi bi-currency-dollar',
      description: 'Amounts payable to funding partners',
      color: '#607D8B'
    }
  ];

  filteredReportTypes: ReportType[] = [];
  companies: Company[] = [];
  selectedReport: ReportType | null = null;

  filter: ReportFilter = {
    reportType: '',
    startDate: '',
    endDate: ''
  };

  searchTerm: string = '';
  isLoading = false;

  // Date constraints
  maxStartDate: string = '';
  maxEndDate: string = '';

  constructor(
    private breadcrumbService: BreadcrumbService,
    private toaster: ToastrService,
    private companyService: CompanyService,
    // private reportService: ReportService // Inject your service here
  ) {
    this.setMaxDates();
    this.dataSubscriber$ = new Subscription();
  }

  ngOnInit(): void {
    this.breadcrumbService.setBreadcrumbs([
      { label: 'Dashboard', url: '/dashboard', icon: 'bi bi-house-door' },
      { label: 'Risk & Compliance Reports', icon: 'bi bi-graph-up-arrow' }
    ]);

    this.breadcrumbService.breadcrumbs$
      .pipe(takeUntil(this.destroy$))
      .subscribe(items => this.breadcrumbItems = items);

    this.filteredReportTypes = [...this.reportTypes];
    this.loadCompanies();
  }

  setMaxDates(): void {
    const today = new Date();
    this.maxStartDate = this.formatDateForInput(today);
    this.maxEndDate = this.formatDateForInput(today);
  }

  formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  getCurrentDate(): string {
    return new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  loadCompanies(): void {
    this.dataSubscriber$.add(
      this.companyService.getExistingCompanies().subscribe({
        next: (companies) => {
          this.companies = companies.data.map((c: any) => ({
            companyId: c.companyId,
            companyName: c.companyName
          }));
        },
        error: (error) => {
          this.toaster.error('Failed to load companies', 'Error');
        }
      })
    );
  }

  filterReports(): void {
    const term = this.searchTerm.toLowerCase().trim();
    if (!term) {
      this.filteredReportTypes = [...this.reportTypes];
      return;
    }

    this.filteredReportTypes = this.reportTypes.filter(report =>
      report.name.toLowerCase().includes(term) ||
      report.description.toLowerCase().includes(term)
    );
  }

  resetSearch(): void {
    this.searchTerm = '';
    this.filterReports();
  }

  openReportFilter(report: ReportType): void {
    this.selectedReport = report;

    // Set default date range (last 30 days)
    this.setDateRange('month');

    this.filter = {
      reportType: report.id,
      companyId: undefined,
      startDate: this.filter.startDate,
      endDate: this.filter.endDate
    };

    const offcanvasEl = document.getElementById('reportFilterOffcanvas');
    if (offcanvasEl) {
      this.offcanvasInstance = new bootstrap.Offcanvas(offcanvasEl);
      this.offcanvasInstance.show();
    }
  }

  handleKeyPress(event: KeyboardEvent, report: ReportType): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.openReportFilter(report);
    }
  }

  onStartDateChange(): void {
    // If end date is before start date, adjust it
    if (this.filter.endDate && this.filter.startDate > this.filter.endDate) {
      this.filter.endDate = this.filter.startDate;
    }
  }

  setDateRange(range: string): void {
    const today = new Date();
    const endDate = new Date(today);
    let startDate = new Date(today);

    switch (range) {
      case 'today':
        startDate = new Date(today);
        break;
      case 'yesterday':
        startDate.setDate(today.getDate() - 1);
        endDate.setDate(today.getDate() - 1);
        break;
      case 'week':
        startDate.setDate(today.getDate() - 7);
        break;
      case 'month':
        startDate.setDate(today.getDate() - 30);
        break;
      case 'quarter':
        startDate.setMonth(today.getMonth() - 3);
        break;
    }

    this.filter.startDate = this.formatDateForInput(startDate);
    this.filter.endDate = this.formatDateForInput(endDate);
  }

  isValidDateRange(): boolean {
    if (!this.filter.startDate || !this.filter.endDate) {
      return false;
    }
    return new Date(this.filter.startDate) <= new Date(this.filter.endDate);
  }

  generateReport(): void {
    if (!this.isValidDateRange()) {
      this.toaster.warning('Please select a valid date range');
      return;
    }

    if (!this.filter.startDate || !this.filter.endDate) {
      this.toaster.warning('Please select both start and end dates');
      return;
    }

    this.isLoading = true;
    this.toaster.info('Generating your report...');

    // TODO: Replace with actual API call to download Excel blob
    // this.reportService.downloadReport(this.filter).subscribe({
    //   next: (blob: Blob) => {
    //     this.downloadBlob(blob);
    //     this.isLoading = false;
    //     this.closeOffcanvas();
    //   },
    //   error: (error) => {
    //     console.error('Error downloading report:', error);
    //     this.toaster.error('Failed to generate report');
    //     this.isLoading = false;
    //   }
    // });

    // Mock download simulation - Remove this in production
    setTimeout(() => {
      this.toaster.success('Excel report downloaded successfully');
      this.isLoading = false;
      this.closeOffcanvas();
    }, 2000);
  }

  downloadBlob(blob: Blob): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;

    // Generate filename with report name and date
    const reportName = this.selectedReport?.name.replace(/\s+/g, '_') || 'report';
    const dateStr = new Date().toISOString().split('T')[0];
    a.download = `${reportName}_${dateStr}.xlsx`;

    document.body.appendChild(a);
    a.click();

    // Cleanup
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  resetFilter(): void {
    this.filter.companyId = undefined;
    this.setDateRange('month');
  }

  closeOffcanvas(): void {
    if (this.offcanvasInstance) {
      this.offcanvasInstance.hide();
    }
  }

  ngOnDestroy(): void {
    if (this.offcanvasInstance) {
      this.offcanvasInstance.dispose();
    }
    this.destroy$.next();
    this.destroy$.complete();
    this.dataSubscriber$.unsubscribe();
  }
}